/*
 * Surfstr language switcher — accessible flag dropdown (button + ARIA listbox).
 * Submits Shopify's native localization form (locale_code + return_to) so the
 * current path is preserved. Falls back to a native <select> when JS is off.
 */
class SurfstrLanguageSwitcher {
  #root;
  #form;
  #input;
  #button;
  #list;
  #options;
  #activeIndex = -1;

  constructor(root) {
    this.#root = root;
    this.#form = root.closest('form');
    this.#button = root.querySelector('[data-surfstr-lang-toggle]');
    this.#list = root.querySelector('[data-surfstr-lang-list]');
    this.#input = this.#form?.querySelector('[data-surfstr-lang-input]');
    this.#options = Array.from(root.querySelectorAll('[role="option"]'));

    if (!this.#form || !this.#button || !this.#list || !this.#input || this.#options.length === 0) return;

    this.#button.addEventListener('click', () => this.#toggle());
    this.#button.addEventListener('keydown', (event) => this.#handleButtonKeydown(event));
    this.#list.addEventListener('keydown', (event) => this.#handleListKeydown(event));

    for (const option of this.#options) {
      option.addEventListener('click', () => this.#select(option));
      option.addEventListener('mousemove', () => this.#setActive(this.#options.indexOf(option)));
    }

    document.addEventListener('click', (event) => this.#handleOutsideClick(event));
  }

  get #isOpen() {
    return this.#button.getAttribute('aria-expanded') === 'true';
  }

  #toggle() {
    if (this.#isOpen) {
      this.#close();
    } else {
      this.#open();
    }
  }

  #open() {
    // Flip the panel above the button when there isn't room below.
    const rect = this.#button.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    this.#root.classList.toggle('surfstr-lang--up', spaceBelow < 280 && rect.top > spaceBelow);

    this.#button.setAttribute('aria-expanded', 'true');
    this.#list.hidden = false;

    const selectedIndex = this.#options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    this.#setActive(selectedIndex >= 0 ? selectedIndex : 0);
    this.#list.focus();
  }

  #close(returnFocus = false) {
    if (!this.#isOpen) return;
    this.#button.setAttribute('aria-expanded', 'false');
    this.#list.hidden = true;
    this.#clearActive();
    if (returnFocus) this.#button.focus();
  }

  #setActive(index) {
    if (index < 0 || index >= this.#options.length) return;
    this.#clearActive();
    this.#activeIndex = index;
    const option = this.#options[index];
    option.classList.add('is-active');
    this.#list.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  }

  #clearActive() {
    for (const option of this.#options) option.classList.remove('is-active');
    this.#list.removeAttribute('aria-activedescendant');
  }

  #select(option) {
    if (!option) return;
    const locale = option.dataset.locale;
    if (!locale) return;
    // Already the current locale → just close, no reload.
    if (option.getAttribute('aria-selected') === 'true') {
      this.#close(true);
      return;
    }
    // The hidden input ships without a name (the noscript <select> owns
    // locale_code when JS is off); claim it here so exactly one field submits.
    this.#input.name = 'locale_code';
    this.#input.value = locale;
    this.#form.submit();
  }

  #handleButtonKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.#open();
        break;
      default:
        break;
    }
  }

  #handleListKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#setActive(Math.min(this.#activeIndex + 1, this.#options.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#setActive(Math.max(this.#activeIndex - 1, 0));
        break;
      case 'Home':
        event.preventDefault();
        this.#setActive(0);
        break;
      case 'End':
        event.preventDefault();
        this.#setActive(this.#options.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.#select(this.#options[this.#activeIndex]);
        break;
      case 'Escape':
        event.preventDefault();
        this.#close(true);
        break;
      case 'Tab':
        this.#close();
        break;
      default:
        break;
    }
  }

  #handleOutsideClick(event) {
    if (!this.#isOpen) return;
    if (this.#root.contains(event.target)) return;
    this.#close();
  }
}

for (const root of document.querySelectorAll('[data-surfstr-lang]')) {
  new SurfstrLanguageSwitcher(root);
}
